import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Tooltip,
  Fade,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import { styled, keyframes } from "@mui/system";
import MicIcon from '@mui/icons-material/Mic';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SpeedIcon from '@mui/icons-material/Speed';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { useNavigation } from '../../../../context/NavigationContext';

// Create waveform animation
const wave = keyframes`
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(3); }
`;

// Styled pulsing recording indicator
const PulsingDot = styled('div')({
  width: '12px',
  height: '12px',
  borderRadius: '50%',
  backgroundColor: '#f44336',
  marginRight: '8px',
  animation: `pulse 1.5s infinite ease-in-out`,
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.7)',
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
    },
    '100%': {
      transform: 'scale(0.95)',
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
    },
  },
});

// Styled components for the audio player
const AudioContainer = styled(Box)({
  padding: '24px',
  borderRadius: '16px',
  backgroundColor: 'rgba(250, 250, 250, 0.7)',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
  marginBottom: '24px',
});

const PlayerContainer = styled(Paper)({
  display: 'flex',
  alignItems: 'center',
  padding: '16px',
  borderRadius: '12px',
  backgroundColor: '#483fc3',
  color: 'white',
  marginBottom: '16px',
});

const PlayButton = styled(IconButton)({
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  padding: 0,
  marginRight: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
});

const SpeedButton = styled(Button)({
  minWidth: '40px',
  height: '30px',
  borderRadius: '15px',
  padding: '0 10px',
  marginLeft: '16px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  color: 'white',
  fontSize: '0.75rem',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
});

const WaveformContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  height: '30px',
  flex: 1,
});

const WaveBar = styled(Box)(({ active, index, isPlaying }) => ({
  width: '4px',
  height: active ? '15px' : '5px',
  backgroundColor: 'white',
  margin: '0 2px',
  borderRadius: '2px',
  opacity: active ? 1 : 0.4,
  animation: isPlaying && active ? `${wave} 1s ease-in-out infinite` : 'none',
  animationDelay: `${index * 0.05}s`,
  transition: 'height 0.3s ease'
}));

// Hint container
const HintContainer = styled(Box)({
  padding: '12px 16px',
  borderRadius: '8px',
  backgroundColor: 'rgba(255, 236, 179, 0.5)',
  border: '1px solid rgba(255, 193, 7, 0.3)',
  display: 'flex',
  alignItems: 'center',
  marginBottom: '16px',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 236, 179, 0.8)',
  }
});

// Recording button
const RecordButton = styled(IconButton)(({ isrecording }) => ({
  p: 1.5,
  backgroundColor: isrecording === 'true' ? 'rgba(244, 67, 54, 0.2)' : 'rgba(25, 118, 210, 0.1)',
  color: isrecording === 'true' ? '#f44336' : '#1976d2',
  border: isrecording === 'true' ? '2px solid #f44336' : 'none',
  transition: 'all 0.3s ease',
  animation: isrecording === 'true' ? 'pulse 1.5s infinite' : 'none',
  '&:hover': {
    backgroundColor: isrecording === 'true' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(25, 118, 210, 0.2)',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.4)',
    },
    '70%': {
      boxShadow: '0 0 0 10px rgba(244, 67, 54, 0)',
    },
    '100%': {
      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)',
    },
  }
}));

// Language selector toggle button
const LanguageToggle = styled(IconButton)({
  position: 'absolute',
  right: '8px',
  top: '8px',
  padding: '4px',
  fontSize: '20px',
  opacity: 0.6,
  '&:hover': {
    opacity: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.05)'
  }
});

const ShadowingInput = ({
  onShadowingSubmit,
  waitingToProceed,
  onProceed,
  correctAnswer,
  audioData,
  audioCdnUrl,
  question,
  language = 'en-US',
  onSkip,
  onLanguageChange
}) => {
  // Course mapping for language recognition codes
  const COURSE_LANGUAGE_MAP = {
    "67cdeb80b567cb0052c62148": "de-DE", // German
    "67ff634626c001d218da332b": "es-ES", // Spanish
    "6801e9392e9e345122975c83": "fr-FR"  // French
  };

  const { currentCourse } = useNavigation();

  // Get audio source
  const getEffectiveAudioSource = () => {
    return audioData || (audioCdnUrl || "");
  };

  // State variables
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [recognition, setRecognition] = useState(null);
  const [audioPlayed, setAudioPlayed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [confidence, setConfidence] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [showHint, setShowHint] = useState(false);
  const [audioError, setAudioError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [recognitionError, setRecognitionError] = useState(null);
  const [showLanguageSettings, setShowLanguageSettings] = useState(false);
  
  // Get language based on course or default
  const getCourseLanguage = () => {
    if (currentCourse?._id && COURSE_LANGUAGE_MAP[currentCourse._id]) {
      return COURSE_LANGUAGE_MAP[currentCourse._id];
    }
    return language;
  };
  
  const [currentLanguage, setCurrentLanguage] = useState(getCourseLanguage());

  // Generate wave visualization data
  const waveformBars = [...Array(30).keys()];
  const [activeWaveBars, setActiveWaveBars] = useState({});

  const audioRef = useRef(null);

  // Update language when course changes
  useEffect(() => {
    const mappedLanguage = getCourseLanguage();
    if (mappedLanguage !== currentLanguage) {
      console.log(`Auto-setting language to ${mappedLanguage} based on course ID ${currentCourse?._id}`);
      setCurrentLanguage(mappedLanguage);
      
      if (recognition) {
        recognition.lang = mappedLanguage;
      }
    }
  }, [currentCourse]);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Initialize speech recognition
  const initSpeechRecognition = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.error('Speech Recognition is not supported in this browser');
      setRecognitionError('Speech Recognition is not supported in this browser');
      return null;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognitionInstance = new SpeechRecognition();

    recognitionInstance.continuous = false;
    recognitionInstance.interimResults = true;
    recognitionInstance.lang = currentLanguage;

    console.log(`Speech recognition initialized with language: ${currentLanguage}`);

    recognitionInstance.onresult = (event) => {
      let interimTranscript = '';
      let finalTranscript = '';
      let highestConfidence = 0;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i][0].confidence > highestConfidence) {
          highestConfidence = event.results[i][0].confidence;
        }

        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      setTranscript(finalTranscript || interimTranscript);
      setConfidence(Math.round(highestConfidence * 100));
    };

    recognitionInstance.onerror = (event) => {
      console.error('Speech recognition error', event.error);
      setRecognitionError(`Recognition error: ${event.error}`);
      setIsListening(false);
    };

    recognitionInstance.onend = () => {
      setIsListening(false);
    };

    return recognitionInstance;
  };

  // Initialize speech recognition when component mounts or language changes
  useEffect(() => {
    const recognitionInstance = initSpeechRecognition();
    setRecognition(recognitionInstance);

    return () => {
      if (recognitionInstance) {
        recognitionInstance.stop();
      }
    };
  }, [currentLanguage]);

  // Start recognition
  const startRecognition = () => {
    if (recognition) {
      recognition.lang = currentLanguage;
      console.log(`Starting recognition with language: ${currentLanguage}`);
      
      try {
        recognition.start();
        setIsListening(true);
        setRecognitionError(null);
      } catch (error) {
        console.error('Error starting speech recognition:', error);
        setRecognitionError(`Error starting recognition: ${error.message}`);
        
        const newRecognition = initSpeechRecognition();
        setRecognition(newRecognition);
        
        setTimeout(() => {
          if (newRecognition) {
            try {
              newRecognition.start();
              setIsListening(true);
            } catch (retryError) {
              console.error('Failed to restart recognition:', retryError);
              setRecognitionError(`Failed to restart: ${retryError.message}`);
              setShowLanguageSettings(true);
            }
          }
        }, 500);
      }
    } else {
      setRecognitionError('Speech recognition not initialized');
      setShowLanguageSettings(true);
    }
  };

  // Audio controls
  const playAudio = () => {
    if (!audioRef.current) return;

    try {
      audioRef.current.playbackRate = playbackSpeed;
      setIsPlaying(true);
      setAudioError(null);
      audioRef.current.play().catch(error => {
        console.error('Error playing audio:', error);
        setIsPlaying(false);
        setAudioError(`Error playing audio: ${error.message}`);
      });
      generateRandomActiveWaveBars();
    } catch (error) {
      console.error('Error playing audio:', error);
      setIsPlaying(false);
      setAudioError(`Error playing audio: ${error.message}`);
    }
  };

  const pauseAudio = () => {
    if (!audioRef.current) return;
    try {
      audioRef.current.pause();
      setIsPlaying(false);
    } catch (error) {
      console.error('Error pausing audio:', error);
    }
  };

  const togglePlayback = () => {
    if (isPlaying) {
      pauseAudio();
    } else {
      playAudio();
    }
  };

  const handleSpeedChange = () => {
    const speeds = [0.5, 0.75, 0.9, 1];
    const currentIndex = speeds.indexOf(playbackSpeed);
    const nextIndex = (currentIndex + 1) % speeds.length;
    setPlaybackSpeed(speeds[nextIndex]);

    if (audioRef.current && isPlaying) {
      audioRef.current.playbackRate = speeds[nextIndex];
    }
  };

  // Generate random active wave bars for visualization
  const generateRandomActiveWaveBars = () => {
    const newActiveWaveBars = {};
    waveformBars.forEach(index => {
      newActiveWaveBars[index] = Math.random() > 0.5;
    });
    setActiveWaveBars(newActiveWaveBars);
  };

  // Update wave visualization during playback
  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(generateRandomActiveWaveBars, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  // Toggle speech recognition
  const toggleListening = () => {
    if (!audioPlayed && !waitingToProceed) {
      return;
    }

    if (recognition) {
      if (isListening) {
        recognition.stop();
      } else {
        setTranscript('');
        setFeedback(null);
        setRecognitionError(null);
        startRecognition();
      }
    }
  };

  // Handle key presses
  const handleKeyDown = (e) => {
    if (e.code === 'Space') {
      e.preventDefault();
      if (waitingToProceed) {
        onProceed();
      } else if (!submitted) {
        if (!isListening) {
          togglePlayback();
        }
      }
    } else if (e.code === 'KeyS') {
      e.preventDefault();
      handleSpeedChange();
    } else if (e.code === 'KeyN') {
      e.preventDefault();
      handleSkip();
    } else if (e.code === 'Enter' && audioPlayed && !isListening && !submitted) {
      e.preventDefault();
      toggleListening();
    } else if (e.code === 'KeyH') {
      e.preventDefault();
      setShowHint(!showHint);
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isListening, waitingToProceed, submitted, isPlaying, audioPlayed, showHint]);

  // Handle language change
  const handleLanguageChange = (newLanguage) => {
    console.log(`Changing recognition language from ${currentLanguage} to ${newLanguage}`);
    setCurrentLanguage(newLanguage);
    
    if (recognition) {
      recognition.lang = newLanguage;
      console.log(`Updated recognition instance language to ${newLanguage}`);
    }
    
    if (onLanguageChange) {
      onLanguageChange(newLanguage);
    }
  };

  const toggleLanguageSettings = () => {
    setShowLanguageSettings(!showLanguageSettings);
  };

  // Enhanced text normalization for comparison
  const normalizeText = (text) => {
    return text.trim().toLowerCase()
      .replace(/[.,!?;:'"()\[\]{}¿¡-]/g, '')
      .replace(/\s+/g, ' ')
      .replace(/\b(um|uh|er|like|so|the|a|an)\b/g, '')
      .replace(/ß/g, 'ss')
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/á/g, 'a')
      .replace(/é/g, 'e')
      .replace(/í/g, 'i')
      .replace(/ó/g, 'o')
      .replace(/ú/g, 'u')
      .replace(/ñ/g, 'n')
      .replace(/ü/g, 'u')
      .trim();
  };

  // Check if two strings are similar enough
  const areTextsSimilar = (text1, text2) => {
    const normalized1 = normalizeText(text1);
    const normalized2 = normalizeText(text2);
    
    if (normalized1 === normalized2) return true;
    
    const words1 = normalized1.split(' ').filter(word => word.length > 0);
    const words2 = normalized2.split(' ').filter(word => word.length > 0);
    
    let matchCount = 0;
    for (const word1 of words1) {
      if (words2.some(word2 => 
        word2 === word1 || 
        (word1.length > 3 && word2.includes(word1)) || 
        (word2.length > 3 && word1.includes(word2))
      )) {
        matchCount++;
      }
    }
    
    const similarity = words1.length > 0 ? matchCount / words1.length : 0;
    console.log(`Similarity: ${similarity * 100}% between "${normalized1}" and "${normalized2}"`);
    
    return similarity >= 0.7; // 70% threshold
  };

  // Handle submitting the answer
  const handleSubmit = () => {
    if (!transcript.trim()) {
      return;
    }

    const isMatch = areTextsSimilar(transcript, correctAnswer);

    setFeedback({
      isCorrect: isMatch,
      transcription: transcript,
      expected: correctAnswer,
      confidence: confidence
    });

    setSubmitted(true);

    if (onShadowingSubmit) {
      onShadowingSubmit(transcript, isMatch, confidence);
    }
  };

  const handleProceed = () => {
    console.log("Proceeding to next card");
    onProceed();
  };

  const handleSkip = () => {
    if (onSkip) {
      console.log("Skipping question - removing permanently");
      onSkip();
    } else {
      // If no skip handler provided, just proceed
      onProceed();
    }
  };

  const toggleHint = () => {
    setShowHint(!showHint);
  };

  // Reinitialize speech recognition
  const reinitializeSpeechRecognition = () => {
    if (isListening && recognition) {
      recognition.stop();
    }
    
    setRecognitionError(null);
    const newRecognition = initSpeechRecognition();
    setRecognition(newRecognition);
    console.log('Speech recognition reinitialized');
  };

  // Reset when waiting state changes
  useEffect(() => {
    if (!waitingToProceed) {
      setIsListening(false);
      setTranscript('');
      setAudioPlayed(false);
      setSubmitted(false);
      setFeedback(null);
      setIsPlaying(false);
      setConfidence(0);
      setPlaybackSpeed(1);
      setShowHint(false);
      setAudioError(null);
      setRecognitionError(null);
      setActiveWaveBars({});
    }
  }, [waitingToProceed]);

  // Check if we have a valid audio source
  const hasAudio = Boolean(getEffectiveAudioSource());

  // Available language options
  const languageOptions = [
    { value: 'en-US', label: 'English (US)' },
    { value: 'de-DE', label: 'German' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'fr-FR', label: 'French' },
    { value: 'it-IT', label: 'Italian' }
  ];

  const shouldShowLanguageSettings = showLanguageSettings || (isMobile && recognitionError);

  return (
    <Box sx={{ mt: 2, position: 'relative' }} onKeyDown={handleKeyDown} tabIndex="0">
      {/* Audio Player */}
      <AudioContainer>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          {question || 'Listen and repeat'}
        </Typography>
        
        {/* Language settings toggle */}
        <LanguageToggle 
          onClick={toggleLanguageSettings}
          title="Speech recognition settings"
          aria-label="Open speech recognition settings"
        >
          <SettingsIcon fontSize="small" />
        </LanguageToggle>

        {/* Language selector */}
        {shouldShowLanguageSettings && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Recognition Language</InputLabel>
              <Select
                value={currentLanguage}
                label="Recognition Language"
                onChange={(e) => handleLanguageChange(e.target.value)}
              >
                {languageOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {recognitionError && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                {recognitionError}
              </Typography>
            )}
            
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<RefreshIcon />}
              onClick={reinitializeSpeechRecognition}
              sx={{ mt: 1, fontSize: '12px' }}
            >
              Reinitialize Speech Recognition
            </Button>
          </Box>
        )}

        {!hasAudio ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="error">
              Audio not available for this question.
            </Typography>
          </Box>
        ) : (
          <PlayerContainer>
            <audio
              ref={audioRef}
              src={getEffectiveAudioSource()}
              onEnded={() => {
                setIsPlaying(false);
                setAudioPlayed(true);
              }}
              onError={(e) => {
                console.error("Audio error:", e);
                setAudioError("Failed to load audio");
                setIsPlaying(false);
              }}
              style={{ display: 'none' }}
            />

            <PlayButton onClick={togglePlayback} disabled={!hasAudio}>
              {isPlaying ? <PauseCircleOutlineIcon /> : <PlayCircleOutlineIcon />}
            </PlayButton>

            <WaveformContainer>
              {waveformBars.map((index) => (
                <WaveBar
                  key={index}
                  active={activeWaveBars[index]}
                  index={index}
                  isPlaying={isPlaying}
                />
              ))}
            </WaveformContainer>

            <SpeedButton onClick={handleSpeedChange} startIcon={<SpeedIcon sx={{ fontSize: '16px' }} />} disabled={!hasAudio}>
              {playbackSpeed}x
            </SpeedButton>
          </PlayerContainer>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Press <strong>Space</strong> to play/pause, <strong>S</strong> to change speed
        </Typography>
      </AudioContainer>

      {/* Hint Feature */}
      {audioPlayed && !submitted && (
        <HintContainer onClick={toggleHint}>
          <LightbulbOutlinedIcon 
            sx={{ 
              color: 'warning.main', 
              mr: 1,
              fontSize: '20px'
            }} 
          />
          <Typography variant="body2" color="text.secondary" fontWeight="medium">
            Having trouble understanding the audio? Click here for help
          </Typography>
        </HintContainer>
      )}

      {/* Hint Text */}
      <Fade in={showHint}>
        <Box 
          sx={{ 
            display: showHint ? 'block' : 'none',
            p: 2, 
            mb: 2,
            bgcolor: 'rgba(255, 248, 225, 0.8)',
            border: '1px dashed rgba(255, 193, 7, 0.5)',
            borderRadius: '8px'
          }}
        >
          <Typography fontWeight="medium" color="text.primary">
            {correctAnswer}
          </Typography>
        </Box>
      </Fade>

      {/* Mic Control */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <Tooltip title={isListening ? "Stop recording" : "Start recording"}>
          <RecordButton
            onClick={toggleListening}
            disabled={!audioPlayed || !hasAudio}
            color={isListening ? "error" : "primary"}
            isrecording={isListening ? "true" : "false"}
            sx={{ p: 1.5 }}
          >
            {isListening ? (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <RadioButtonCheckedIcon sx={{ color: '#f44336' }} />
              </Box>
            ) : (
              <MicIcon />
            )}
          </RecordButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="textSecondary" align="center" sx={{ mb: 1 }}>
        {!hasAudio ? 'Audio not available' :
          !audioPlayed ? 'Listen to the audio first' :
          isListening ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <PulsingDot />
              <Typography variant="body2" color="error.main" fontWeight="medium">
                Recording... Speak now
              </Typography>
            </Box>
          ) : 'Click the microphone or press Enter to start speaking'}
      </Typography>

      {/* Transcript Box */}
      <Box
        sx={{
          p: 2,
          minHeight: '60px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: isListening ? 'error.main' : 'divider',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          position: 'relative'
        }}
      >
        {isListening && (
          <Box sx={{ display: 'flex', alignItems: 'center', position: 'absolute', top: 8, right: 8 }}>
            <PulsingDot />
            <Typography variant="caption" color="error">
              Recording
            </Typography>
          </Box>
        )}

        {transcript ? (
          <Typography>{transcript}</Typography>
        ) : (
          <Typography color="text.secondary">
            {waitingToProceed ? 'Continue to the next card' : 'Your spoken text will appear here'}
          </Typography>
        )}
      </Box>

      {/* Feedback Box */}
      {feedback && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: feedback.isCorrect ? 'success.light' : 'error.light',
            borderRadius: 1,
            color: feedback.isCorrect ? 'success.contrastText' : 'error.contrastText'
          }}
        >
          <Typography fontWeight="bold">
            {feedback.isCorrect ? 'Excellent pronunciation!' : 'Try using the hint above to see the correct pronunciation'}
          </Typography>
          
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
            You said: <span style={{ fontStyle: 'italic' }}>{feedback.transcription}</span>
          </Typography>
          <Typography variant="caption" sx={{ mt: 0.5, display: 'block', opacity: 0.8 }}>
            Confidence: {feedback.confidence}%
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
        {submitted ? (
          <Button
            variant="contained"
            color="primary"
            onClick={handleProceed}
          >
            Continue
          </Button>
        ) : (
          <>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<SkipNextIcon />}
              onClick={handleSkip}
            >
              Skip
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!transcript.trim() || isListening || !hasAudio}
            >
              Submit
            </Button>
          </>
        )}
      </Box>

      {/* Keyboard shortcuts help */}
      <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 2, display: 'block' }}>
        Keyboard shortcuts: <strong>Enter</strong> to start recording, <strong>N</strong> to skip, <strong>H</strong> for hint
      </Typography>
    </Box>
  );
};

export default ShadowingInput;